import * as Sentry from '@sentry/react';
import type { FeatureFlagsIntegration } from '@sentry/react';

/**
 * The signed-in recruiter, and the flags they were evaluated under, on every
 * subsequent Sentry event.
 *
 * This module imports the Sentry SDK and nothing else. It is reachable from
 * `containers/AuthRoute`, which sits in the graph the whole app loads; pulling
 * anything heavier in here would reorder evaluation for every entry that
 * reaches it.
 *
 * Every function here is inert when no client is running, which is what
 * `Sentry.setUser` and friends already do, so an unconfigured build behaves the
 * same as a configured one minus the reporting.
 */

export interface SentryIdentity {
  external_id?: string;
  account_id?: string;
  role?: string;
  is_impersonating?: boolean;
}

/**
 * Attach the signed-in identity.
 *
 * The external id alone. No email, no name: "users affected" counts distinct
 * ids, and the id is the only part of that an issue needs.
 *
 * An impersonated session reports no user. `whoami` describes the customer
 * user being impersonated and carries no id for the Fountain employee acting
 * as them, so the only id on offer would count a support session among the
 * customer's affected users. The `impersonated` tag still marks its events.
 */
export function setSentryUser(whoami: SentryIdentity): void {
  Sentry.setUser(whoami.is_impersonating ? null : { id: whoami.external_id });
  Sentry.setTag('account', whoami.account_id);
  Sentry.setTag('role', whoami.role);
  // `is_impersonating`, not `is_impersonated`: the latter is also true for a
  // super user signed in to their own account.
  Sentry.setTag('impersonated', Boolean(whoami.is_impersonating));
}

export function clearSentryUser(): void {
  Sentry.setUser(null);
  Sentry.setTag('account', undefined);
  Sentry.setTag('role', undefined);
  Sentry.setTag('impersonated', undefined);
}

/**
 * Report the flags this session is under, so an issue says which of them the
 * user had.
 *
 * `whoami.feature_flags` is LaunchDarkly's full client-side flag state, off
 * flags included, and anything but `true` is reported as off. Sentry buffers
 * the most recent hundred, so an account with more client-side flags than that
 * loses the oldest.
 */
export function setSentryFeatureFlags(
  flags: Record<string, boolean | undefined> | undefined,
): void {
  if (!flags) {
    return;
  }

  const integration =
    Sentry.getClient()?.getIntegrationByName<FeatureFlagsIntegration>(
      'FeatureFlags',
    );

  if (!integration) {
    return;
  }

  Object.entries(flags).forEach(([name, value]) => {
    integration.addFeatureFlag(name, value === true);
  });
}
